import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  CanalVenta,
  CanalesVentaPagingRes,
  UseFetchEnabledParams,
} from '@/shared/interfaces';
import { getUrlParamsFetch, handleAxiosError } from '@/shared/utils';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchCanalesVenta = ({
  page = 1,
  page_size = 10,
  nombre = '',
  supervisor = '',
}: GetCanalesVentaParams) => {
  return useQuery({
    queryKey: ['canalesVenta', page, nombre, supervisor],
    queryFn: async () => {
      return getCanalesVenta({ page, nombre, supervisor, page_size });
    },
  });
};

export const useFetchCanalVentaAll = ({
  params,
  enabled = true,
}: UseFetchEnabledParams<GetCanalesVentaParams> = {}) => {
  return useQuery({
    queryKey: ['canalesVenta', ...Object.values(params ?? {})],
    queryFn: () => getCanalesVenta(params),
    enabled: enabled,
  });
};

export const useGetCanalVenta = (id: string) => {
  return useQuery({
    queryKey: ['canalesVenta', id],
    queryFn: async () => {
      return getCanalVenta(id);
    },
  });
};

export const useCreateCanalVenta = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createCanalVenta,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canalesVenta'] });
      setAlert(
        true,
        'Canal de venta creado correctamente',
        AlertSeverityEnum.success
      );
      navigate(returnUrl);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: err => {
      handleAxiosError(err);
    },
  });
};

export const useUpdateCanalVenta = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCanalVenta,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canalesVenta'] });
      setAlert(
        true,
        'Canal de venta actualizado correctamente',
        AlertSeverityEnum.success
      );

      navigate(returnUrl);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: err => {
      handleAxiosError(err);
    },
  });
};

///* axios
export type GetCanalesVentaParams = Partial<CanalVenta> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateCanalVentaParams = Omit<CanalVenta, 'id_canal_venta'>;
export type UpdateCanalVentaParams = {
  id: string;
  data: CreateCanalVentaParams;
};

export const getCanalesVenta = async (params?: GetCanalesVentaParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<CanalesVentaPagingRes>(
    `${urlBackend}api/v1/canal-venta/?${queryParams}`,
    true
  );
};

export const getCanalVenta = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<CanalVenta>(
    `${urlBackend}api/v1/canal-venta/${id}/`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createCanalVenta = async (data: CreateCanalVentaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<CanalVenta>(`${urlBackend}api/v1/canal-venta/`, data, true);
};

export const updateCanalVenta = async ({
  id,
  data,
}: UpdateCanalVentaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<CanalVenta>(`${urlBackend}api/v1/canal-venta/${id}/`, data, true);
};
